import { useState, useEffect, useCallback, useRef } from "react";
import { X, MessageSquare } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConversatieModal({ user, token, onClose, onUnreadCleared }) {
  const [berichten, setBerichten] = useState([]);
  const [tekst,     setTekst]     = useState("");
  const [sending,   setSending]   = useState(false);
  const [loading,   setLoading]   = useState(true);
  const bottomRef = useRef(null);

  const laadBerichten = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/users/${user.id}/berichten`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setBerichten(d.berichten || []);
      onUnreadCleared(user.id);
    } catch {}
    setLoading(false);
  }, [user.id, token, onUnreadCleared]);

  useEffect(() => { laadBerichten(); }, [laadBerichten]);
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [berichten]);

  const handleStuur = async () => {
    if (!tekst.trim() || sending) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/admin/users/${user.id}/berichten`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tekst: tekst.trim() }),
      });
      const d = await r.json();
      setBerichten(prev => [...prev, d]);
      setTekst("");
    } catch {}
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-[#111] border border-white/10 rounded-2xl flex flex-col animate-fadeIn"
        style={{ height: "min(85vh, 600px)" }}>

        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
              <span className="text-[#C9A84C] text-xs font-bold">
                {user.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-white font-bold text-sm">{user.name}</p>
              <p className="text-zinc-500 text-xs">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {loading ? (
            <p className="text-zinc-600 text-sm text-center pt-8">Laden...</p>
          ) : berichten.length === 0 ? (
            <div className="text-center pt-12">
              <MessageSquare size={28} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">Nog geen berichten</p>
              <p className="text-zinc-700 text-xs mt-1">Stuur een bericht om de conversatie te starten</p>
            </div>
          ) : berichten.map(b => {
            const isTrainer = b.van === "trainer";
            return (
              <div key={b.id} className={`flex ${isTrainer ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[75%]">
                  <div className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    isTrainer
                      ? "text-black rounded-br-sm"
                      : "bg-white/8 text-zinc-200 rounded-bl-sm border border-white/6"
                  }`} style={isTrainer ? { background: "#C9A84C" } : {}}>
                    {b.tekst}
                  </div>
                  <p className={`text-[10px] text-zinc-600 mt-1 ${isTrainer ? "text-right" : "text-left"}`}>
                    {new Date(b.datum).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        <div className="px-4 pb-4 pt-3 border-t border-white/8">
          <div className="flex gap-2">
            <input
              type="text"
              value={tekst}
              onChange={e => setTekst(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleStuur()}
              placeholder="Typ een bericht..."
              data-testid="conversatie-input"
              className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-[#C9A84C]/40"
            />
            <button onClick={handleStuur} disabled={!tekst.trim() || sending}
              data-testid="stuur-bericht-btn"
              className="px-4 py-2.5 rounded-xl text-sm font-bold text-black disabled:opacity-40 transition-opacity"
              style={{ background: "#C9A84C" }}>
              {sending ? "..." : "Stuur"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
